
import 'jquery';
import { fn } from './utils';

/** Marker CSS class for a custom style added in runtime */
const cssCustomStyle = "idms_CustomStyle";

/** Static style container for custom styles and all media types */
var customStyleContainer: HTMLStyleElement;
/** Named array with all custom styles */
var customStyles: { [cssName: string]: CSSStyleRule } = {};

/** 'Structured' Description for Customization of HTML Markup */
export interface IMarkupCustomization {
    /* Unique Identifier (matches generated css rule) */
    Id: string;
    /** optional Description*/
    Description?: string;
    /** Custom BackColor (name or hex) */
    BackColor?: string;
    /** Custom TextColor (css) */
    TextColor?: string;
}

/** Ensure that a custom style is defined and ready to use by its class name */
export function EnsureStyle(markup: IMarkupCustomization) {
    var styleId = GetStyleID(markup);
    var cssRule = <CSSStyleDeclaration>{ color: markup.TextColor, backgroundColor: markup.BackColor };
    AddCSSClass("." + styleId, cssRule);
    return styleId;
}

/** Apply a custom style to JQuery element(s), returning a function to reset the applied style */
export function ApplyStyle(element: JQuery, markup: IMarkupCustomization) {
    var applyStyle = markup && (markup.BackColor || markup.TextColor),
        applyTitle = markup && markup.Description;

    if (applyStyle) {
        var styleName = GetStyleID(markup);
        element.addClass(cssCustomStyle);
        element.addClass(styleName);
    }
    if (applyTitle) {
        var pTitle = element.attr("title");
        element.attr("title", markup.Description);
    }

    function removeAppliedStyle() {
        if (applyStyle) {
            element.removeClass(cssCustomStyle);
            element.removeClass(styleName);
        }

        if (applyTitle) {
            element.attr("title", pTitle);
        }
    }

    return removeAppliedStyle;
}

/** Remove a custom style previously added by "EnsureStyle" */
export function RemoveStyle(markup: IMarkupCustomization) {
    RemoveCSSClass("." + GetStyleID(markup));
}

/**
 * Adds a CSS class to the styles library
 * @param className Name of the CSS class (including "." prefix)
 * @param rules CSS rules (defined in {})
 */
function AddCSSClass(className: string, rules: CSSStyleDeclaration) {

    if (className in customStyles) {
        // Modify existing style if already exists
        let cssRule = customStyles[className];
        ApplyCSS(cssRule, rules);
    } else {
        // Add new style
        let newRule = AddCSSRule(className, rules);
        customStyles[className] = newRule;
    }
}

/** Removes a custom style by its identifier */
function RemoveCSSClass(className: string) {
    if (className in customStyles) {
        var existingStyle = customStyles[className],
            sheet = GetSheet(),
            cssRuleIndex = fn.indexOf(sheet && sheet.cssRules, existingStyle);

        if (cssRuleIndex >= 0) {
            sheet.deleteRule(cssRuleIndex);
        }
        delete customStyles[className];
    }
}

/** Add a CSS rule to the custom styles (must be created first - see AddCSSClass) */
function AddCSSRule(className: string, rules: CSSStyleDeclaration) {
    var sheet = GetSheet();
    if (sheet) {
        let cssStr = GetCSSStringFromDeclaration(rules),
            idx = sheet.insertRule(className + "{" + cssStr + "}", 0),
            cssRules = sheet.cssRules;

        return cssRules && <CSSStyleRule>cssRules.item(idx);
    }
}

/** Applies a new style to an existing CSS rule */
function ApplyCSS(cssRule: CSSStyleRule, style: CSSStyleDeclaration) {
    if (cssRule && cssRule.style && style) {
        for (var key in style) {
            var strKey = GetStyleHyphenFormat(<string>key);
            var val = style[key];
            cssRule.style.setProperty(strKey, val);
        }
    }
}

/** Converts a style declaration to a string that is needed for CSSStyleSheet.insertRule */
function GetCSSStringFromDeclaration(style: CSSStyleDeclaration) {
    var retVal = "";
    if (style) {
        for (var key in style) {
            var strKey = GetStyleHyphenFormat(<string>key);
            var strVal = style[key];

            retVal += strKey + ":" + strVal + ";";
        }
    }
    return retVal;
}

/** Get or imp create dynamic stylesheet */
function GetSheet() {
    return <CSSStyleSheet>GetStyleContainer().sheet;
}

/** Get or imp create dynamic style element */
function GetStyleContainer() {
    var result = customStyleContainer;

    if (!result) {
        result = customStyleContainer = document.createElement('style');
        result.type = 'text/css';
        result.title = "InfomaDynamicStyles";
        document.getElementsByTagName('head')[0].appendChild(result);
    }

    return result;
}

/** Convert styling properties like "backgroundColor" to "background-color" */
function GetStyleHyphenFormat(propertyName: string) {
    function upperToHyphenLower(match) {
        return '-' + match.toLowerCase();
    }
    return propertyName && propertyName.replace(/[A-Z]/g, upperToHyphenLower);
}

/** Get Style as Class ('EnsureStyle' must be explicitly called before) */
function GetStyleID(markup: IMarkupCustomization) {
    // replace : compatibility (id was guid!)
    var styleId = "idms_CustomStyle_" + (markup.Id || (markup.Id = fn.id())).replace(/\-/g, "");
    return styleId;
}
